/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import { Button } from "@adobe/react-spectrum";
import {
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsLayoutProps } from "@jsonforms/react";
import {
  getHeaders,
  getEndpointName,
  getBaseUrl,
} from "@apollo/jsonforms-react-spectrum-renderers/src/util/commonUtils";
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// Lodash
import { get, set } from "lodash";

// Redux
import { useSelector } from "react-redux";
import Loader from "../components/Loader/Loader";
import { FormContext } from "../context/FormsContext";
import { DYNAMIC_FORMS, GENERIC_ERROR } from "../utils/constants";
import displayToasts from "../utils/displayToasts";
import * as konsole from "../utils/konsole";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";

export const submitButtonRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("SubmitButton")
);

/**
 * Custom renderer for a submit button.
 */
function SubmitButtonRenderer({ uischema, visible, enabled }: RendererProps) {
  const style = uischema.options?.style;
  const name = uischema.options?.name;
  const spectrumProps = uischema.options?.spectrumProps;
  const label = uischema.options?.label;
  const method = uischema.options?.method;
  const redirect = uischema.options?.redirect;
  const success = uischema.options?.success;
  const errorMessage = uischema.options?.error;
  let submitUrl = uischema.options?.submitUrl;
  const downloadData = uischema.options?.downloadData;
  const downloadFormat = uischema.options?.downloadFormat;
  const fileName = uischema.options?.fileName;
  // Disabled when there are errors
  const disableUntilFilled = uischema.options?.disableUntilFilled;
  const endpointPath = uischema.options?.endpointPath;
  const type = uischema.options?.type;
  const localStorageProps = uischema.options?.localStorageProps || [];
  const { requestDataParams } = uischema.options?.requestDataParams || [];
  const queryParams = uischema.options?.queryParams || [];
  const pageContextParams = uischema.options?.pageContextParams || [];
  const isDisabled = uischema.options?.spectrumProps?.isDisabled || false;
  const staticData = uischema.options?.staticData;
  const requiredRequestParams = uischema.options?.requiredRequestParams;
  const requestDataKey = uischema.options?.requestDataKey;
  const reloadDynamicSection = uischema.options?.reloadDynamicSection || false;
  const sessionStorageDataKey = uischema.options?.sessionStorageDataKey;
  const apiKeyMappingForSubmit = uischema.options?.apiKeyMappingForSubmit;

  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [submitButtonDisable, setSubmitButtonDisable] = useState(isDisabled);

  const [_, setContext] = useContext<any>(FormContext);

  const jsonFormContext = useJsonForms();
  const errors = jsonFormContext?.core?.errors || {};
  const data = jsonFormContext?.core?.data || {};
  const config = jsonFormContext?.config || {};

  if (endpointPath) {
    const pathKey = getEndpointName(endpointPath, config);
    const endpoint = get(config, pathKey, "");
    submitUrl = endpoint + submitUrl;
  }
  const params = {};
  if (localStorageProps) {
    localStorageProps.forEach((key: any) => {
      try {
        const value = localStorage.getItem(key);
        params[key] = value;
      } catch (error) {
        /* empty */
      }
    });
  }
  if (queryParams) {
    queryParams.forEach((key: any) => {
      const value = get(data, key, "");
      params[key] = value;
    });
  }

  if (pageContextParams) {
    pageContextParams.forEach((key: any) => {
      const value = useSelector((state: any) =>
        get(state?.reduxState, key, "")
      );
      params[key] = value;
    });
  }

  if (Object.keys(params).length > 0) {
    submitUrl = getBaseUrl(submitUrl, params);
  }

  const onSuccessCallback = jsonFormContext?.config?.onSuccessCallback;
  const onErrorCallback = jsonFormContext?.config?.onErrorCallback;
  const onSubmitSuccess =
    jsonFormContext?.config?.lifecycleHooks?.onSubmitSuccess;

  const onSubmitError = jsonFormContext?.config?.lifecycleHooks?.onSubmitError;

  const onSubmitCallbacks =
    jsonFormContext?.config?.lifecycleHooks?.onSuccessCallbacks?.submitButton;

  const handleSubmitButton = onSubmitCallbacks?.handleSubmitButton;
  const handleSubmitData = onSubmitCallbacks?.handleSubmitData;

  const setFormContext =
    jsonFormContext?.config?.lifecycleHooks?.setFormContext;

  const preprocess =
    jsonFormContext?.config?.lifecycleHooks?.submit?.preprocess;

  if (handleSubmitButton) {
    handleSubmitButton({ submitButtonDisable, setSubmitButtonDisable });
  }

  /**
   * set the formData to the context data
   * @param formData - form data
   */
  const setFormData = (formData: any, formTouched?: boolean) => {
    setContext((oldValues: any) => ({
      ...oldValues,
      data: formData,
      config: {
        ...oldValues?.config,
        formTouched: formTouched || false,
      },
    }));
  };

  useEffect(() => {
    // call the setformData
    if (setFormContext) {
      setFormContext({ setFormData });
    }
  }, []);

  const externalHandlers =
    jsonFormContext?.config?.lifecycleHooks?.externalHandlers;

  const externalSubmitHandler = externalHandlers?.onSubmit;
  const submitFormDataTransformed = externalHandlers?.submitFormDataTransformed;

  // const headers = new Headers();
  const headers = getHeaders(jsonFormContext?.config?.apiHeaders);
  headers.append("content-type", "application/json;charset=UTF-8");
  const clearForm = () => {
    setContext((oldValues: any) => ({
      ...oldValues,
      data: {},
      config: { ...oldValues?.config, formTouched: false },
    }));
  };

  const resetForm = () => {
    setContext((oldValues: any) => ({
      ...oldValues,
      data: {},
      config: {
        ...oldValues?.config,
        formTouched: false,
      },
    }));
  };

  const downloadFile = (dataTransformed: any) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(
      new Blob([JSON.stringify(dataTransformed)], {
        type: downloadFormat === "json" ? "application/json" : "text/plain",
      })
    );
    a.download = `${fileName}.${downloadFormat}`;
    a.click();
    resetForm();
    if (redirect?.type) {
      clearForm();
      const redirectType = redirect?.type;
      switch (redirectType) {
        case "route":
          navigate(redirect?.path);
          break;

        default:
          break;
      }
    } else if (success?.message) {
      displayToasts({
        type: "success",
        text: success?.message,
        autoClose: true,
      });
    }
  };

  const makeFetchAPICall = (fetchParams: any) => {
    fetch(submitUrl, fetchParams)
      .then(async (response) => {
        if (response.ok) {
          // Call the success callback passed from parent
          if (onSuccessCallback) {
            onSuccessCallback();
          }
          konsole.log(`${DYNAMIC_FORMS}: Submit Success`);

          if (onSubmitSuccess) {
            let responseData = {};
            try {
              responseData = await response.json();
            } catch {
              responseData = {};
            }
            onSubmitSuccess(responseData);
            if (handleSubmitData) {
              handleSubmitData({ data });
            }
          }
          // if after success response user wants to reload the dynamic section
          if (reloadDynamicSection) {
            setContext((oldValues: any) => ({
              ...oldValues,
              config: {
                ...oldValues?.config,
                formTouched: false,
                dynamicSection: { reload: true },
              },
            }));
          }
          if (redirect?.type) {
            clearForm();
            const redirectType = redirect?.type;
            switch (redirectType) {
              case "route":
                navigate(redirect?.path);
                break;

              default:
                break;
            }
          } else if (success?.message) {
            displayToasts({
              type: "success",
              text: success?.message,
              autoClose: true,
            });
          }
        } else {
          const error = await response.json();
          konsole.log(`${DYNAMIC_FORMS}: Submit Error`);
          if (onErrorCallback) {
            onErrorCallback();
          }
          if (onSubmitError) {
            onSubmitError(error);
          }
          if (errorMessage?.message) {
            displayToasts({
              type: "error",
              text: error?.message,
              autoClose: true,
            });
          } else if (error?.message) {
            displayToasts({
              type: "error",
              text: error?.message,
              autoClose: true,
            });
          } else {
            displayToasts({
              type: "error",
              text: errorMessage?.genericMessage || GENERIC_ERROR,
              autoClose: true,
            });
          }
        }
      })
      .catch((error) => {
        konsole.log(`${DYNAMIC_FORMS}: Submit Error`);
        if (onSubmitError) {
          onSubmitError(error);
        }

        displayToasts({
          type: "error",
          text: errorMessage?.genericMessage || GENERIC_ERROR,
          autoClose: true,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSubmitAfterDataTransformed = (dataTransformed: any) => {
    if (downloadFormat) {
      downloadFile(dataTransformed);
    } else {
      const fetchParams = {
        method: method || "POST",
        body: JSON.stringify(dataTransformed),
        headers,
      };
      makeFetchAPICall(fetchParams);
    }
  };

  if (submitFormDataTransformed) {
    submitFormDataTransformed({ handleSubmitAfterDataTransformed });
  }

  const transformDataAPIKeyMap = (requestBodyData: any) => {
    const localData = requestBodyData;
    Object.keys(apiKeyMappingForSubmit).forEach((objKey) => {
      set(localData, apiKeyMappingForSubmit[objKey], get(localData, objKey));
      delete localData[objKey];
    });
    return localData;
  };

  const handleFormSubmit = () => {
    setContext((oldValues: any) => ({
      ...oldValues,
      data,
      config: {
        ...oldValues?.config,
        formTouched: true,
        dynamicSection: { reload: false },
      },
    }));

    if (
      !submitUrl &&
      externalSubmitHandler &&
      Object.keys(errors).length === 0
    ) {
      const obj = {
        ...data,
        ...(type && { type }),
      };
      externalSubmitHandler(obj, type ? resetForm : clearForm);
    } else if (downloadData && Object.keys(errors).length === 0) {
      downloadFile(
        apiKeyMappingForSubmit ? transformDataAPIKeyMap(data) : data
      );
    } else if (!downloadData && submitUrl && Object.keys(errors).length === 0) {
      setSubmitting(true);
      let fetchParams;
      if (method && method === "GET") {
        fetchParams = {
          method,
          headers,
        };
      } else {
        let requestBodyParams = data;
        if (requestDataParams) {
          let paramData = {};
          requestDataParams.forEach((param: any) => {
            const requestData = get(data, param);
            paramData = { ...paramData, ...requestData };
          });
          requestBodyParams = paramData;
        }
        if (staticData) {
          requestBodyParams = { ...requestBodyParams, ...staticData };
        }
        if (requiredRequestParams) {
          const paramData = Object.fromEntries(
            Object.entries(requestBodyParams).filter(([key]) =>
              requiredRequestParams.includes(key)
            )
          );
          requestBodyParams = paramData;
        }
        if (requestDataKey) {
          requestBodyParams = { [requestDataKey]: requestBodyParams };
        }

        if (sessionStorageDataKey) {
          let sessionData: any =
            sessionStorage.getItem(sessionStorageDataKey) || {};
          sessionData = JSON.parse(sessionData);
          requestBodyParams = { ...sessionData, ...requestBodyParams };
        }

        // changing the keys in the request body obj to requirement keys provided as mapping input
        if (apiKeyMappingForSubmit) {
          requestBodyParams = transformDataAPIKeyMap(requestBodyParams);
        }
        // if there is a preprocess function, call it to transform the request body
        if (preprocess) {
          requestBodyParams = preprocess(name, requestBodyParams);
        }
        fetchParams = {
          method: method || "POST",
          body: JSON.stringify(requestBodyParams),
          headers,
        };
      }
      makeFetchAPICall(fetchParams);
    }
  };
  let disableButtonStyle = {};
  if (
    submitButtonDisable ||
    (disableUntilFilled && Object.keys(errors).length > 0)
  ) {
    disableButtonStyle = { backgroundColor: "#F4F4F4", border: "none" };
  }

  // Determine if the component should be visible and enabled
  let componentVisibility = visible;
  let componentEnabled = enabled;

  // If there is a context rule, evaluate it and use that visibility/enabled rule instead.
  if (uischema.options?.contextRule) {
    // grab the scope from the contextRule
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    // get the redux store state based on the scope provided
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    const contextRuleValidation = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    );
    componentVisibility = contextRuleValidation?.visible;
    componentEnabled = contextRuleValidation?.enabled;
  }

  return (
    <>
      <Button
        {...spectrumProps}
        UNSAFE_style={{ ...style, cursor: "pointer", ...disableButtonStyle }}
        onPress={handleFormSubmit}
        isDisabled={
          submitButtonDisable ||
          (disableUntilFilled && Object.keys(errors).length > 0) ||
          !componentEnabled
        }
        data-testid="button"
        isHidden={!componentVisibility}
      >
        {label}
      </Button>

      {submitting && <Loader fullScreen />}
    </>
  );
}

export default withJsonFormsLayoutProps(SubmitButtonRenderer);
